/**
 * Bulk / batch SIEM bodies → timeline pick list or single id fields.
 */

import type { DropTimelineContext, DropTimelinePickTarget } from './types';

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function isPreparedUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    s.trim(),
  );
}

function parseWiserDropId(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isInteger(v) && v >= 1) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isInteger(n) && n >= 1) return n;
  }
  return undefined;
}

function shortPreparedLabel(id: string): string {
  const t = id.trim();
  if (t.length <= 12) return t;
  return `${t.slice(0, 8)}…`;
}

export function bulkPreparedContextFromDetails(
  details: Record<string, unknown> | null,
): DropTimelineContext {
  if (!details) return {};
  const req = asRecord(details.requestBody);
  if (!req) return {};
  const ids = req.ids;
  if (!Array.isArray(ids)) return {};
  const uuids = ids
    .filter((x): x is string => typeof x === 'string')
    .map((s) => s.trim())
    .filter(isPreparedUuid);
  if (uuids.length === 0) return {};
  if (uuids.length === 1) return { preparedDropId: uuids[0] };
  const pickTargets: DropTimelinePickTarget[] = uuids.map((id) => ({
    label: `Prepared ${shortPreparedLabel(id)}`,
    preparedDropId: id,
  }));
  return { pickTargets };
}

export function editBatchContextFromDetails(
  details: Record<string, unknown> | null,
): DropTimelineContext {
  if (!details) return {};
  const req = asRecord(details.requestBody);
  if (!req) return {};
  const payloads = req.payloads;
  if (!Array.isArray(payloads)) return {};
  const wids: number[] = [];
  for (const p of payloads) {
    const o = asRecord(p);
    const w = o ? parseWiserDropId(o.dropId) : undefined;
    if (w != null) wids.push(w);
  }
  if (wids.length === 0) return {};
  if (wids.length === 1) return { wiserDropId: wids[0] };
  const pickTargets: DropTimelinePickTarget[] = wids.map((wid) => ({
    label: `SMSWiser #${wid}`,
    wiserDropId: wid,
  }));
  return { pickTargets };
}

function mergePickLists(
  a: DropTimelinePickTarget[] | undefined,
  b: DropTimelinePickTarget[] | undefined,
): DropTimelinePickTarget[] | undefined {
  const merged = [...(a ?? []), ...(b ?? [])];
  if (merged.length === 0) return undefined;
  const seen = new Set<string>();
  const out: DropTimelinePickTarget[] = [];
  for (const t of merged) {
    const k = `${t.preparedDropId ?? ''}|${t.wiserDropId ?? ''}|${t.uniqueId ?? ''}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

export function mergeCtx(a: DropTimelineContext, b: DropTimelineContext): DropTimelineContext {
  const picks = mergePickLists(a.pickTargets, b.pickTargets);
  return {
    ...(a.uniqueId != null ? { uniqueId: a.uniqueId } : {}),
    ...(a.wiserDropId != null ? { wiserDropId: a.wiserDropId } : {}),
    ...(a.preparedDropId != null ? { preparedDropId: a.preparedDropId } : {}),
    ...(b.uniqueId != null && a.uniqueId == null ? { uniqueId: b.uniqueId } : {}),
    ...(b.wiserDropId != null && a.wiserDropId == null ? { wiserDropId: b.wiserDropId } : {}),
    ...(b.preparedDropId != null && a.preparedDropId == null
      ? { preparedDropId: b.preparedDropId }
      : {}),
    ...(picks != null ? { pickTargets: picks } : {}),
  };
}

export function collapseSinglePickTarget(ctx: DropTimelineContext): DropTimelineContext {
  const pts = ctx.pickTargets;
  if (pts?.length !== 1) return ctx;
  const t = pts[0];
  const base: DropTimelineContext = {
    ...(ctx.uniqueId != null ? { uniqueId: ctx.uniqueId } : {}),
    ...(ctx.wiserDropId != null ? { wiserDropId: ctx.wiserDropId } : {}),
    ...(ctx.preparedDropId != null ? { preparedDropId: ctx.preparedDropId } : {}),
  };
  return mergeCtx(base, {
    ...(t.uniqueId != null ? { uniqueId: t.uniqueId } : {}),
    ...(t.wiserDropId != null ? { wiserDropId: t.wiserDropId } : {}),
    ...(t.preparedDropId != null ? { preparedDropId: t.preparedDropId } : {}),
  });
}
